<template>
  <div class="stock-management">
    <!-- Header avec statistiques -->
    <div class="stock-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <i class="fas fa-boxes"></i>
            Gestion de Stock
          </h1>
          <p class="page-subtitle">Gérez vos produits, mouvements et inventaire</p>
        </div>
        
        <!-- Cartes de catégories avec pagination -->
        <div class="categories-section">
          <div class="categories-wrapper">
            <div class="categories-cards">
              <div 
                v-for="category in paginatedCategories" 
                :key="category._id"
                class="category-card"
                :class="{ 'active': selectedCategory === category.nom }"
                :style="{ '--category-color': category.couleur }"
                @click="filterByCategory(category.nom)"
              >
                <div class="category-emoji">{{ category.emoji }}</div>
                <div class="category-content">
                  <div class="category-count">{{ category.nombreProduits }}</div>
                  <div class="category-name">{{ category.nom }}</div>
                </div>
                <div class="category-overlay"></div>
              </div>
            </div>
            <div class="pagination-controls" v-if="totalCategoryPages > 1">
              <button 
                class="pagination-btn"
                :disabled="currentCategoryPage === 1"
                @click="previousCategoryPage"
                title="Page précédente"
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              <span class="pagination-info">
                {{ currentCategoryPage }} / {{ totalCategoryPages }}
              </span>
              <button 
                class="pagination-btn"
                :disabled="currentCategoryPage === totalCategoryPages"
                @click="nextCategoryPage"
                title="Page suivante"
              >
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation par onglets -->
    <div class="tabs-navigation">
      <div class="tabs-container">
        <button 
          v-for="tab in tabs" 
          :key="tab.key"
          class="tab-button"
          :class="{ active: activeTab === tab.key }"
          @click="setActiveTab(tab.key)"
        >
          <i :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
          <span v-if="tab.badge" class="tab-badge">{{ tab.badge }}</span>
        </button>
      </div>
    </div>

    <!-- Contenu principal -->
    <div class="main-content">
      <transition name="fade" mode="out-in">
        <template v-if="activeTab === 'products'">
          <div key="products" class="tab-content">
            <ProductsManager 
              :products="products"
              :loading="loading"
              :selectedCategory="selectedCategory"
              @refresh="loadProducts"
              @create="showCreateProductModal"
              @edit="showEditProductModal"
              @delete="deleteProduct"
              @give-products="openGiveProductsModal"
            />
          </div>
        </template>

        <template v-else-if="activeTab === 'movements'">
          <div key="movements" class="tab-content">
            <MovementsManager 
              :movements="movements"
              :loading="loading"
              @refresh="loadMovements"
              @create="showCreateMovementModal"
            />
          </div>
        </template>

        <template v-else-if="activeTab === 'bons'">
          <div key="bons" class="tab-content">
            <BonsManager 
              :bons="bons"
              :loading="loading"
              @refresh="loadBons"
              @create="showCreateBonModal"
              @return-to-stock="handleReturnToStock"
            />
          </div>
        </template>

        <template v-else-if="activeTab === 'alerts'">
          <div key="alerts" class="tab-content">
            <AlertsManager 
              :alerts="alerts"
              :loading="loading"
              @refresh="loadAlerts"
            />
          </div>
        </template>

        <template v-else-if="activeTab === 'techreport'">
          <div key="techreport" class="tab-content">
            <TechnicianProductsReport />
          </div>
        </template>
      </transition>
    </div>

    <!-- Modales -->
    <ProductModal 
      v-if="showProductModal"
      :product="selectedProduct"
      :mode="modalMode"
      @close="closeProductModal"
      @save="saveProduct"
    />

    <MovementModal 
      v-if="showMovementModal"
      :movement="selectedMovement"
      @close="closeMovementModal"
      @save="saveMovement"
    />

    <BonModal 
      v-if="showBonModal"
      :bon="selectedBon"
      :type="bonType"
      @close="closeBonModal"
      @save="saveBon"
    />

    <PackageModal 
      v-if="showPackageModal"
      :package="selectedPackage"
      :mode="modalMode"
      :availableProducts="products"
      @close="closePackageModal"
      @save="savePackage"
    />

    <PackageDetailModal 
      v-if="showPackageDetailModal"
      :package="selectedPackage"
      @close="closePackageDetailModal"
      @edit="editFromDetail"
    />

    <GiveProductsToTechnicianModal
      v-if="showGiveProductsModal"
      @close="closeGiveProductsModal"
      @success="handleProductsGiven"
    />

    <!-- Notifications -->
    <NotificationContainer />
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import ProductsManager from '@/components/stock/ProductsManager.vue'
import MovementsManager from '@/components/stock/MovementsManager.vue'
import BonsManager from '@/components/stock/BonsManager.vue'
import AlertsManager from '@/components/stock/AlertsManager.vue'
import PackagesManager from '@/components/stock/PackagesManager.vue'
import ProductModal from '@/components/stock/ProductModal.vue'
import MovementModal from '@/components/stock/MovementModal.vue'
import BonModal from '@/components/stock/BonModal.vue'
import PackageModal from '@/components/stock/PackageModal.vue'
import PackageDetailModal from '@/components/stock/PackageDetailModal.vue'
import GiveProductsToTechnicianModal from '@/components/stock/GiveProductsToTechnicianModal.vue'
import TechnicianProductsReport from '@/components/stock/TechnicianProductsReport.vue'
import NotificationContainer from '@/components/common/NotificationContainer.vue'
import stockService from '@/services/stockService'
import categoryService from '@/services/categoryService'

export default {
  name: 'Stock',
  components: {
    ProductsManager,
    MovementsManager,
    BonsManager,
    AlertsManager,
    PackagesManager,
    ProductModal,
    MovementModal,
    BonModal,
    PackageModal,
    PackageDetailModal,
    GiveProductsToTechnicianModal,
    TechnicianProductsReport,
    NotificationContainer
  },
  setup() {
    const store = useStore()
    
    // État réactif
    const activeTab = ref('products')
    const loading = ref(false)
    const products = ref([])
    const movements = ref([])
    const bons = ref({ sortie: [], retour: [] })
    const alerts = ref([])
    const packages = ref([])
    const dashboard = ref({})
    const categories = ref([])
    const selectedCategory = ref(null)
    const currentCategoryPage = ref(1)
    const categoriesPerPage = 6
    
    // Modales
    const showProductModal = ref(false)
    const showMovementModal = ref(false)
    const showBonModal = ref(false)
    const showPackageModal = ref(false)
    const showPackageDetailModal = ref(false)
    const showGiveProductsModal = ref(false)
    const selectedProduct = ref(null)
    const selectedMovement = ref(null)
    const selectedBon = ref(null)
    const selectedPackage = ref(null)
    const modalMode = ref('create')
    const bonType = ref('sortie')

    // Configuration des onglets
    const userRole = computed(() => store.getters['auth/userRole'])
    
    const tabs = computed(() => {
      const baseTabs = [
        {
          key: 'products',
          label: 'Produits',
          icon: 'fas fa-box',
          badge: products.value.length || null
        },
        {
          key: 'movements',
          label: 'Mouvements',
          icon: 'fas fa-exchange-alt',
          badge: null
        },
        {
          key: 'bons',
          label: 'Bons',
          icon: 'fas fa-file-alt',
          badge: dashboard.value.bonsEnCours || null
        },
        {
          key: 'alerts',
          label: 'Alertes',
          icon: 'fas fa-exclamation-triangle',
          badge: alerts.value.length || null
        }
      ]
      
      // Ajouter l'onglet Rapport Techniciens pour Magasinier et Admin
      if (userRole.value === 'Magasinier' || userRole.value === 'Admin') {
        baseTabs.push({
          key: 'techreport',
          label: 'Rapport Techniciens',
          icon: 'fas fa-user-cog',
          badge: null
        })
      }
      
      return baseTabs
    })

    // Pagination des catégories
    const totalCategoryPages = computed(() => {
      return Math.ceil(categories.value.length / categoriesPerPage)
    })

    const paginatedCategories = computed(() => {
      const start = (currentCategoryPage.value - 1) * categoriesPerPage
      const end = start + categoriesPerPage
      return categories.value.slice(start, end)
    })

    const previousCategoryPage = () => {
      if (currentCategoryPage.value > 1) {
        currentCategoryPage.value--
      }
    }

    const nextCategoryPage = () => {
      if (currentCategoryPage.value < totalCategoryPages.value) {
        currentCategoryPage.value++
      }
    }

    // Méthodes
    const setActiveTab = (tab) => {
      activeTab.value = tab
      loadTabData(tab)
    }

    const loadTabData = async (tab) => {
      loading.value = true
      try {
        switch (tab) {
          case 'products':
            await loadProducts()
            break
          case 'movements':
            await loadMovements()
            break
          case 'bons':
            await loadBons()
            break
          case 'alerts':
            await loadAlerts()
            break
          case 'packages':
            await loadPackages()
            break
        }
      } catch (error) {
        console.error(`Erreur lors du chargement de l'onglet ${tab}:`, error)
        // Erreur silencieuse - ne pas afficher de notification pour ne pas perturber l'utilisateur
      } finally {
        loading.value = false
      }
    }

    const loadDashboard = async () => {
      try {
        const response = await stockService.getDashboard()
        dashboard.value = response.data
      } catch (error) {
        console.error('Erreur lors du chargement du tableau de bord:', error)
      }
    }

    const loadCategories = async () => {
      try {
        const response = await categoryService.getCategoryStats()
        categories.value = response.data.data || []
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error)
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: 'Erreur lors du chargement des catégories'
        })
      }
    }

    const filterByCategory = (categoryName) => {
      if (selectedCategory.value === categoryName) {
        // Désélectionner si on clique sur la même catégorie
        selectedCategory.value = null
      } else {
        selectedCategory.value = categoryName
      }
      // Passer au tab produits et appliquer le filtre
      activeTab.value = 'products'
    }

    const loadProducts = async () => {
      try {
        // Charger TOUS les produits (limite élevée pour éviter la pagination)
        const response = await stockService.getProducts({ limit: 1000 })
        products.value = response.data
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error)
        throw error
      }
    }

    const loadMovements = async () => {
      try {
        const response = await stockService.getMovements()
        movements.value = response.data
      } catch (error) {
        console.error('Erreur lors du chargement des mouvements:', error)
        throw error
      }
    }

    const loadBons = async () => {
      try {
        // Charger les données du rapport techniciens pour les bons de sortie
        // et les produits à retourner pour les bons de retour
        const [sortieResponse, retourResponse] = await Promise.all([
          stockService.getTechniciansReport(), // Rapport des produits en sac
          stockService.getRetoursReport() // Rapport des produits à retourner
        ])
        
        // Transformer les données du rapport en format compatible avec BonsManager
        const rapportSortie = sortieResponse.data?.rapport || []
        const rapportRetour = retourResponse.data?.rapport || []
        
        bons.value = {
          sortie: rapportSortie, // Les données du rapport techniciens (produits en sac)
          retour: rapportRetour // Les données des produits à retourner
        }
        console.log('📦 Bons chargés depuis rapports:')
        console.log('  - Sortie (en sac):', rapportSortie.length, 'techniciens')
        console.log('  - Retour (à retourner):', rapportRetour.length, 'techniciens')
      } catch (error) {
        console.error('Erreur lors du chargement des bons:', error)
        throw error
      }
    }

    const loadAlerts = async () => {
      try {
        const response = await stockService.getAlerts()
        alerts.value = response.data
      } catch (error) {
        console.error('Erreur lors du chargement des alertes:', error)
        throw error
      }
    }

    const loadPackages = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/packages', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        const data = await response.json()
        packages.value = data.packages || []
      } catch (error) {
        console.error('Erreur lors du chargement des packages:', error)
        throw error
      }
    }

    // Gestion des modales - Produits
    const showCreateProductModal = () => {
      selectedProduct.value = null
      modalMode.value = 'create'
      showProductModal.value = true
    }

    const showEditProductModal = (product) => {
      selectedProduct.value = product
      modalMode.value = 'edit'
      showProductModal.value = true
    }

    const closeProductModal = () => {
      showProductModal.value = false
      selectedProduct.value = null
    }

    const saveProduct = async (productData) => {
      try {
        if (modalMode.value === 'create') {
          const response = await stockService.createProduct(productData)
          // Vérifier si la création a réussi (response.success ou response.data existe)
          if (response?.success !== false && (response?.success === true || response?.data)) {
            store.dispatch('notifications/addNotification', {
              type: 'success',
              message: 'Produit créé avec succès'
            })
            closeProductModal()
            await loadProducts()
            await loadDashboard()
            await loadCategories()
            return // Important: sortir de la fonction après succès
          }
        } else {
          await stockService.updateProduct(selectedProduct.value._id, productData)
          store.dispatch('notifications/addNotification', {
            type: 'success',
            message: 'Produit mis à jour avec succès'
          })
          closeProductModal()
          await loadProducts()
          await loadDashboard()
          return
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du produit:', error)
        
        // Ignorer les erreurs de référence dupliquée si le produit a été créé
        // (le backend peut renvoyer cette erreur même après création réussie)
        const errorMessage = error.response?.data?.message || error.message || ''
        
        // Si l'erreur mentionne "référence" et qu'on est en mode création,
        // vérifier si le produit a quand même été créé
        if (errorMessage.includes('référence') && modalMode.value === 'create') {
          // Recharger les produits pour vérifier
          await loadProducts()
          // Ne pas afficher l'erreur, le produit a probablement été créé
          closeProductModal()
          await loadDashboard()
          await loadCategories()
          return
        }
        
        let displayMessage = 'Erreur lors de la sauvegarde du produit'
        
        // Gestion spécifique de l'erreur de référence dupliquée
        if (error.response?.status === 400 && errorMessage.includes('E11000')) {
          const duplicateKey = errorMessage.match(/dup key: { (\w+): "([^"]+)" }/);
          if (duplicateKey) {
            const field = duplicateKey[1];
            const value = duplicateKey[2];
            if (field === 'referenceInterne') {
              displayMessage = `La référence "${value}" existe déjà. Veuillez choisir une autre référence.`;
            } else {
              displayMessage = `La valeur "${value}" pour le champ "${field}" existe déjà.`;
            }
          } else {
            displayMessage = 'Cette référence existe déjà. Veuillez choisir une autre référence.';
          }
        } else if (errorMessage) {
          displayMessage = errorMessage
        }
        
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: displayMessage
        })
      }
    }

    const deleteProduct = async (product) => {
      if (confirm(`Êtes-vous sûr de vouloir supprimer le produit "${product.designation}" ?`)) {
        try {
          await stockService.deleteProduct(product._id)
          store.dispatch('notifications/addNotification', {
            type: 'success',
            message: 'Produit supprimé avec succès'
          })
          await loadProducts()
          await loadDashboard()
        } catch (error) {
          console.error('Erreur lors de la suppression du produit:', error)
          store.dispatch('notifications/addNotification', {
            type: 'error',
            message: 'Erreur lors de la suppression du produit'
          })
        }
      }
    }

    // Gestion des modales - Mouvements
    const showCreateMovementModal = () => {
      selectedMovement.value = null
      showMovementModal.value = true
    }

    const closeMovementModal = () => {
      showMovementModal.value = false
      selectedMovement.value = null
    }

    const saveMovement = async (movementData) => {
      try {
        await stockService.createMovement(movementData)
        store.dispatch('notifications/addNotification', {
          type: 'success',
          message: 'Mouvement créé avec succès'
        })
        closeMovementModal()
        await loadMovements()
        await loadDashboard()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du mouvement:', error)
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: 'Erreur lors de la sauvegarde du mouvement'
        })
      }
    }

    // Gestion des modales - Bons
    const showCreateBonModal = (type = 'sortie') => {
      selectedBon.value = null
      bonType.value = type
      showBonModal.value = true
    }

    const closeBonModal = () => {
      showBonModal.value = false
      selectedBon.value = null
    }

    const saveBon = async (bonData) => {
      try {
        if (bonType.value === 'sortie') {
          await stockService.createBonSortie(bonData)
        } else {
          await stockService.createBonRetour(bonData)
        }
        store.dispatch('notifications/addNotification', {
          type: 'success',
          message: `Bon de ${bonType.value} créé avec succès`
        })
        closeBonModal()
        await loadBons()
        await loadDashboard()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du bon:', error)
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: 'Erreur lors de la sauvegarde du bon'
        })
      }
    }

    // Retourner un produit au stock
    const handleReturnToStock = async (product) => {
      try {
        // Demander confirmation
        if (!confirm(`Voulez-vous retourner le produit "${product.designation}" au stock ?\n\nCela changera son statut à "en_stock" et le retirera du technicien.`)) {
          return
        }

        loading.value = true
        
        // Appeler l'API pour retourner le produit au stock
        await stockService.returnProductToStock(product._id)
        
        // Afficher une notification de succès
        store.dispatch('notifications/addNotification', {
          type: 'success',
          message: `Produit "${product.designation}" retourné au stock avec succès`
        })
        
        // Recharger les données
        await loadBons()
        await loadProducts()
        await loadDashboard()
      } catch (error) {
        console.error('Erreur lors du retour du produit au stock:', error)
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors du retour du produit au stock'
        })
      } finally {
        loading.value = false
      }
    }

    // Gestion des modales - Donner Produits au Technicien
    const openGiveProductsModal = () => {
      showGiveProductsModal.value = true
    }

    const closeGiveProductsModal = () => {
      showGiveProductsModal.value = false
    }

    const handleProductsGiven = async () => {
      await loadProducts()
      await loadDashboard()
    }

    // Gestion des modales - Packages
    const showCreatePackageModal = () => {
      selectedPackage.value = null
      modalMode.value = 'create'
      showPackageModal.value = true
    }

    const showEditPackageModal = (pkg) => {
      selectedPackage.value = pkg
      modalMode.value = 'edit'
      showPackageModal.value = true
    }

    const showViewPackageModal = (pkg) => {
      selectedPackage.value = pkg
      showPackageDetailModal.value = true
    }

    const closePackageModal = () => {
      showPackageModal.value = false
      selectedPackage.value = null
    }

    const closePackageDetailModal = () => {
      showPackageDetailModal.value = false
      selectedPackage.value = null
    }

    const editFromDetail = (pkg) => {
      closePackageDetailModal()
      showEditPackageModal(pkg)
    }

    const savePackage = async (packageData) => {
      try {
        const token = localStorage.getItem('token')
        const url = modalMode.value === 'create' 
          ? '/api/packages'
          : `/api/packages/${selectedPackage.value._id}`
        
        const response = await fetch(url, {
          method: modalMode.value === 'create' ? 'POST' : 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(packageData)
        })

        if (!response.ok) {
          throw new Error('Erreur lors de la sauvegarde')
        }

        store.dispatch('notifications/addNotification', {
          type: 'success',
          message: `Package ${modalMode.value === 'create' ? 'créé' : 'modifié'} avec succès`
        })
        closePackageModal()
        await loadPackages()
        await loadDashboard()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde du package:', error)
        store.dispatch('notifications/addNotification', {
          type: 'error',
          message: 'Erreur lors de la sauvegarde du package'
        })
      }
    }

    const deletePackage = async (packageId) => {
      if (confirm('Êtes-vous sûr de vouloir supprimer ce package ?')) {
        try {
          const token = localStorage.getItem('token')
          const response = await fetch(`/api/packages/${packageId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })

          if (!response.ok) {
            throw new Error('Erreur lors de la suppression')
          }

          store.dispatch('notifications/addNotification', {
            type: 'success',
            message: 'Package supprimé avec succès'
          })
          await loadPackages()
          await loadDashboard()
        } catch (error) {
          console.error('Erreur lors de la suppression du package:', error)
          store.dispatch('notifications/addNotification', {
            type: 'error',
            message: 'Erreur lors de la suppression du package'
          })
        }
      }
    }

    // Initialisation
    onMounted(async () => {
      await loadDashboard()
      await loadCategories()
      await loadTabData(activeTab.value)
    })

    return {
      // État
      activeTab,
      loading,
      products,
      movements,
      bons,
      alerts,
      packages,
      dashboard,
      
      // Modales
      showProductModal,
      showMovementModal,
      showBonModal,
      showPackageModal,
      showPackageDetailModal,
      showGiveProductsModal,
      selectedProduct,
      selectedMovement,
      selectedBon,
      selectedPackage,
      modalMode,
      bonType,
      
      // Computed
      tabs,
      categories,
      selectedCategory,
      currentCategoryPage,
      totalCategoryPages,
      paginatedCategories,
      
      // Méthodes
      setActiveTab,
      loadProducts,
      loadMovements,
      loadBons,
      loadAlerts,
      loadPackages,
      showCreateProductModal,
      showEditProductModal,
      closeProductModal,
      saveProduct,
      deleteProduct,
      showCreateMovementModal,
      closeMovementModal,
      saveMovement,
      showCreateBonModal,
      closeBonModal,
      saveBon,
      handleReturnToStock,
      showCreatePackageModal,
      showEditPackageModal,
      showViewPackageModal,
      closePackageModal,
      closePackageDetailModal,
      editFromDetail,
      savePackage,
      deletePackage,
      openGiveProductsModal,
      closeGiveProductsModal,
      handleProductsGiven,
      filterByCategory,
      previousCategoryPage,
      nextCategoryPage
    }
  }
}
</script>

<style scoped>
.stock-management {
  min-height: 100vh;
  background: #ffffff;
  padding: 0;
}

/* Header */
.stock-header {
  background: rgba(255, 255, 255, 1);
  border-bottom: 2px solid rgba(0, 27, 183, 0.1);
  padding: 2rem 0;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.08);
}

.stock-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.02), rgba(0, 70, 255, 0.01));
  opacity: 1;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  position: relative;
  z-index: 1;
}

.title-section {
  color: var(--primary-blue);
  position: relative;
  z-index: 1;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-title i {
  font-size: 2rem;
  opacity: 0.9;
  background: linear-gradient(45deg, var(--primary-blue), var(--secondary-blue));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
  margin: 0;
  font-weight: 500;
}

/* Section Catégories */
.categories-section {
  flex: 1;
  min-width: 0;
  margin-top: 2rem;
}

.categories-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.categories-cards {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 1rem;
  width: 100%;
}

.category-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(15px);
  border: 2px solid rgba(0, 27, 183, 0.15);
  border-radius: 16px;
  padding: 1.25rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.08);
  position: relative;
  overflow: hidden;
  min-height: 130px;
}

.category-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.03), rgba(0, 70, 255, 0.02));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.category-card:hover::before {
  opacity: 1;
}

.category-card:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 27, 183, 0.15);
  border-color: var(--category-color, var(--primary-blue));
  background: rgba(255, 255, 255, 1);
}

.category-card.active {
  border-color: var(--category-color, var(--primary-blue));
  border-width: 3px;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.06), rgba(0, 70, 255, 0.04));
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.2);
}

.category-card.active::before {
  opacity: 1;
}

.category-card.active::after {
  content: '✓';
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: var(--category-color, var(--primary-blue));
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.category-emoji {
  font-size: 2.5rem;
  line-height: 1;
  transition: transform 0.3s ease;
  z-index: 1;
}

.category-card:hover .category-emoji {
  transform: scale(1.15) rotate(8deg);
}

.category-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-align: center;
  z-index: 1;
  width: 100%;
}

.category-count {
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--category-color, var(--primary-blue));
  line-height: 1;
}

.category-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.5rem;
}

.pagination-btn {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.65rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.2);
}

.pagination-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 27, 183, 0.3);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}

.pagination-info {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary-blue);
  padding: 0.5rem 1rem;
  background: rgba(0, 27, 183, 0.05);
  border-radius: 10px;
  min-width: 60px;
  text-align: center;
}

/* Navigation par onglets - Design compact */
.tabs-navigation {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 27, 183, 0.1);
  padding: 0 2rem;
  box-shadow: 0 2px 12px rgba(0, 27, 183, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
}

.tabs-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 0;
}

.tab-button {
  background: transparent;
  border: 1px solid rgba(0, 27, 183, 0.1);
  padding: 0.75rem 1.5rem;
  color: var(--primary-blue);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  min-height: 44px;
  white-space: nowrap;
}

.tab-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  opacity: 0;
  border-radius: 12px;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.tab-button:hover::before {
  opacity: 0.05;
}

.tab-button:hover {
  color: var(--primary-blue);
  transform: translateY(-1px);
  border-color: rgba(0, 27, 183, 0.2);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.1);
}

.tab-button.active {
  color: white;
  border-color: var(--primary-blue);
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  box-shadow: 0 4px 16px rgba(0, 27, 183, 0.25);
}

.tab-button.active::before {
  opacity: 0;
}

.tab-button i {
  font-size: 1rem;
  opacity: 0.9;
}

.tab-badge {
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(255, 71, 87, 0.3);
  animation: pulse 2s infinite;
  line-height: 1;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

/* Contenu principal */
.main-content {
  background: #ffffff;
  min-height: calc(100vh - 200px);
  padding: 2rem;
}

.tab-content {
  max-width: 1400px;
  margin: 0 auto;
}

/* Animations */
.fade-enter-active, .fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.95);
}

/* Responsive */
@media (max-width: 1400px) {
  .categories-cards {
    grid-template-columns: repeat(5, 1fr);
    gap: 0.85rem;
  }
  
  .category-card {
    padding: 1.1rem 0.9rem;
    min-height: 120px;
  }
  
  .category-emoji {
    font-size: 2.2rem;
  }
  
  .category-count {
    font-size: 1.6rem;
  }
  
  .category-name {
    font-size: 0.75rem;
  }
}

@media (max-width: 1200px) {
  .categories-cards {
    grid-template-columns: repeat(4, 1fr);
    gap: 0.75rem;
  }
  
  .category-card {
    padding: 1rem 0.85rem;
    min-height: 115px;
  }
  
  .category-emoji {
    font-size: 2rem;
  }
  
  .category-count {
    font-size: 1.5rem;
  }
  
  .category-name {
    font-size: 0.7rem;
  }

  .pagination-btn {
    padding: 0.6rem 1.25rem;
    font-size: 0.8rem;
  }

  .pagination-info {
    font-size: 0.8rem;
    padding: 0.45rem 0.85rem;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .page-title {
    font-size: 2rem;
  }

  .categories-section {
    width: 100%;
    margin-top: 1rem;
  }

  .categories-wrapper {
    gap: 1rem;
  }

  .categories-cards {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.65rem;
  }

  .category-card {
    padding: 0.9rem 0.75rem;
    min-height: 105px;
  }
  
  .category-emoji {
    font-size: 1.8rem;
  }
  
  .category-count {
    font-size: 1.3rem;
  }
  
  .category-name {
    font-size: 0.65rem;
  }

  .pagination-btn {
    padding: 0.55rem 1.1rem;
    font-size: 0.75rem;
  }

  .pagination-info {
    font-size: 0.75rem;
    padding: 0.4rem 0.75rem;
  }

  .tabs-navigation {
    padding: 0 1rem;
  }

  .tabs-container {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.5rem 0;
  }

  .tab-button {
    padding: 0.6rem 1.2rem;
    font-size: 0.8rem;
    min-height: 40px;
  }

  .tab-button i {
    font-size: 0.9rem;
  }

  .main-content {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 0 1rem;
  }

  .page-title {
    font-size: 1.75rem;
  }

  .page-subtitle {
    font-size: 0.95rem;
  }

  .categories-section {
    margin-top: 0.75rem;
  }

  .categories-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
  
  .category-card {
    padding: 0.85rem 0.65rem;
    min-height: 100px;
  }

  .category-emoji {
    font-size: 1.6rem;
  }

  .category-count {
    font-size: 1.2rem;
  }

  .category-name {
    font-size: 0.6rem;
  }

  .pagination-btn {
    padding: 0.5rem 0.9rem;
    font-size: 0.7rem;
  }

  .pagination-info {
    font-size: 0.7rem;
    padding: 0.35rem 0.65rem;
  }

  .tabs-navigation {
    padding: 0 0.5rem;
  }

  .tabs-container {
    gap: 0.25rem;
    padding: 0.5rem 0;
  }

  .tab-button {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    min-height: 36px;
    flex: 1;
    justify-content: center;
  }

  .tab-button span:not(.tab-badge) {
    display: none;
  }

  .tab-button i {
    font-size: 1.1rem;
    margin: 0;
  }

  .tab-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    font-size: 0.6rem;
    padding: 0.15rem 0.4rem;
    min-width: 16px;
  }
}

/* Scrollbar personnalisée */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, var(--secondary-blue), var(--primary-blue));
}
</style>
